import React, { useState } from 'react'
import '../../css/consent-modal.scss'
import './study-consent.scss'

// Study-specific consent.
//
// Deliberately NOT an edit to component/student-components/ConsentModal — that one is
// shared with the classroom deployments, where students are NOT recorded and the data
// genuinely is anonymous. Editing it in place would make the class consent claim things
// that aren't true of those sessions. Same stylesheet, different content.
//
// Two claims from the classroom version had to change outright for this study:
//   - "We do not collect camera or microphone data" — we now record both.
//   - "anonymous ... cannot be connected to you personally" — a recording of someone's
//     face is the opposite of anonymous. This study is CONFIDENTIAL, not anonymous, and
//     saying otherwise in a consent form is the kind of error an IRB exists to catch.
export default function StudyConsentModal ({ onConsent, isProcessing }) {
  const [isChecked, setIsChecked] = useState(false)

  return (
    <div className="consent-modal-overlay study-consent">
      <div className="consent-modal-content">
        <div className="consent-body">

          <div className="consent-section">
            <h2>About this study</h2>
            <p>
              You’re taking part in a research study about how people learn a programming
              library, and how that learning holds up afterwards. It runs in three parts:
              the short session you already completed, this session (about 80–90 minutes),
              and a 20-minute follow-up 6–8 days from now.
            </p>
            <p className="irb-number">IRB #26-039 — Virginia Tech</p>
          </div>

          <div className="consent-section">
            <h2>Recording</h2>
            <p>
              <strong>This session and the follow-up are recorded.</strong> For the whole
              of each session we ask you to record:
            </p>
            <ul>
              <li>Your <strong>entire screen</strong> — not just this browser tab</li>
              <li>Your <strong>camera</strong></li>
            </ul>
            <p>
              Parts of this study are done with an AI tutor available, and parts are
              deliberately done on your own. The recording is how we confirm that the
              on-your-own parts really were — it’s what makes the comparison fair for
              everyone taking part, including you. You start and stop the recording
              yourself, and we’ll tell you exactly when.
            </p>
            <p>
              We review recordings only where the activity data suggests we should. If we
              have questions about a session, the recording is what we look at.
            </p>
          </div>

          <div className="consent-section">
            <h2>What else we collect</h2>
            <p>Alongside the recording, the platform logs your activity in the task:</p>
            <ul>
              <li>Mouse movements, clicks, and when you switch away from the task tab</li>
              <li>Code changes, copy/paste, and the output of code you run</li>
              <li>Your messages with the AI tutor, where it is available</li>
              <li>Your answers to the surveys and quiz</li>
            </ul>
          </div>

          <div className="consent-section">
            <h2>Privacy</h2>
            <p>
              Your data is <strong>confidential, but not anonymous</strong>. Because we
              record your camera and pay you for taking part, the research team can link
              your data to you. Your work is stored under a participant code rather than
              your name, and recordings and data are viewable only by the research team on
              secure storage. Nothing is shared with anyone outside the team, and nothing
              published from this research will identify you.
            </p>
            <p>
              Recordings are deleted once the sessions they cover have been checked, and no
              later than three months after the study ends. Please avoid including personal
              information in your messages to the AI tutor.
            </p>
          </div>

          <div className="consent-section">
            <h2>Voluntary participation</h2>
            <p>
              Taking part is voluntary. You can stop at any point, and you can ask us to
              delete your recordings and data afterwards, without giving a reason and
              without affecting any compensation you’ve already earned. If you’d rather not
              be recorded, you can decline here — that ends your participation, and it has
              no other consequences.
            </p>
          </div>

          <div className="consent-section">
            <h2>Questions?</h2>
            <p>
              Contact Dr. Yan Chen at <a href="mailto:ych@vt.edu">ych@vt.edu</a> or Shiva
              Pochampally at <a href="mailto:shivapochampally@vt.edu">shivapochampally@vt.edu</a>. For
              questions about your rights as a participant, contact the Virginia Tech HRPP
              Office at 540-231-3732 (<a href="mailto:irb@vt.edu">irb@vt.edu</a>).
            </p>
          </div>

          <div className="consent-checkbox-section">
            <label>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                disabled={isProcessing}
              />
              <span>
                I have read the above and agree to take part. I understand that this
                session and the follow-up are recorded, including my screen and camera,
                and that I can withdraw at any time.
              </span>
            </label>
          </div>
        </div>

        <div className="consent-footer">
          <button className="btn-decline" onClick={() => !isProcessing && onConsent(false)} disabled={isProcessing}>
            Decline
          </button>
          <button
            className="btn-accept"
            onClick={() => isChecked && !isProcessing && onConsent(true)}
            disabled={!isChecked || isProcessing}
          >
            {isProcessing ? 'Processing…' : 'I Consent'}
          </button>
        </div>
      </div>
    </div>
  )
}
